/*
 * WorkspaceDocumentCenter — Five-Tab Document Center for Client Workspace.
 *
 * Extracted from WorkspaceTabs.jsx per Management authorization 2026-02.
 * Implements the approved file-folder tab UI with five distinct pastel folder tabs:
 *   1. Uploaded Documents   — soft blue
 *   2. Requested Documents  — soft amber
 *   3. Document Health      — soft green
 *   4. Package Intelligence — soft lavender
 *   5. Missing Advisories   — soft rose
 *
 * Architecture rules:
 *   - Frontend-only change. No backend modifications.
 *   - All APIs, components, and data sources reused as-is.
 *   - Canonical taxonomy (CAT_LABELS, SECTION_GROUPS) preserved verbatim.
 *   - Package Intelligence is NOT auto-triggered on tab open.
 *   - Last selected folder tab persisted in localStorage per client.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import {
  SectionCard, EmptyState, DataTable, Pill, StatusBadge,
} from "@/components/shared";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DocumentHealthStrip from "@/components/documents/DocumentHealthStrip";
import PackageIntelligencePanel from "@/components/documents/PackageIntelligencePanel";
import MissingDocumentAdvisoriesPanel from "@/components/documents/MissingDocumentAdvisoriesPanel";
import LockedUploadDialog from "@/components/documents/LockedUploadDialog";
import StaffUploadDialog from "@/components/documents/StaffUploadDialog";
import { ExistingDocumentReuse } from "@/components/documents/ExistingDocumentReuse";

// ─── Canonical Taxonomy ───────────────────────────────────────────────────────
// Source of truth — do not alter. Mockup category examples are visual references only.
const CAT_LABELS = {
  identity:           "Identity & Immigration",
  prior_tax_returns:  "Tax Returns",
  income_documents:   "Income Documents",
  w2:                 "W-2 Forms",
  "1099":             "1099 Forms",
  k1:                 "K-1 Forms",
  financial_statements:"Financial Statements",
  banking:            "Banking",
  foreign_accounts:   "Foreign Accounts",
  fbar_support:       "FBAR Support",
  fatca_support:      "FATCA Support",
  business_records:   "Business & Entity Documents",
  investments:        "Investments",
  rental_property:    "Property & Real Estate",
  crypto:             "Cryptocurrency",
  irs_notices:        "Tax Notices",
  correspondence:     "Correspondence",
  legal_documents:    "Legal & Authorizations",
  engagement_letters: "Engagement Documents",
  organizer_support:  "Questionnaires & Responses",
  deliverables:       "Deliverables",
  filing_confirmations: "Filing Confirmations",
  thai_tax:           "Thai Tax",
  thai_tin:           "Thai TIN",
  other:              "Other / Unclassified",
  reference_material: "Reference Material",
  advisory_research:  "Advisory / Research",
};
const CAT_ORDER = Object.keys(CAT_LABELS);

// Presentation groupings over canonical taxonomy (display order = definition order)
const SECTION_GROUPS = [
  { key: "tax_documents",          label: "Tax Documents",              cats: new Set(["prior_tax_returns", "w2", "1099", "k1", "thai_tax"]) },
  { key: "identity",               label: "Identity & Personal",        cats: new Set(["identity", "thai_tin"]) },
  { key: "financial",              label: "Financial & Banking",        cats: new Set(["banking", "investments", "foreign_accounts", "fbar_support", "fatca_support", "crypto", "rental_property", "financial_statements"]) },
  { key: "business",               label: "Business",                   cats: new Set(["business_records"]) },
  { key: "correspondence",         label: "Correspondence & Notices",   cats: new Set(["correspondence", "irs_notices", "legal_documents", "engagement_letters"]) },
  // D-5: Deliverables & Engagement section — authorized 2026-02 (Document Taxonomy Consistency workstream)
  { key: "engagement_deliverables",label: "Deliverables & Engagement",  cats: new Set(["deliverables", "filing_confirmations"]) },
  { key: "other",                  label: "Other",                      cats: new Set(["other", "reference_material", "advisory_research", "organizer_support", "supporting_documentation"]) },
];
const CAT_TO_SECTION = {};
SECTION_GROUPS.forEach((sg) => sg.cats.forEach((c) => { CAT_TO_SECTION[c] = sg.key; }));

const DOC_PROC_BADGE = {
  quarantined:             { label: "Security Hold",      cls: "bg-rose-100 text-rose-700" },
  pending_security_review: { label: "Security Hold",      cls: "bg-rose-100 text-rose-700" },
  processing_failed:       { label: "Failed",             cls: "bg-rose-100 text-rose-700" },
  partially_ready:         { label: "Degraded",           cls: "bg-amber-100 text-amber-700" },
  uploaded:                { label: "Processing",         cls: "bg-blue-100 text-blue-700" },
  security_scanning:       { label: "Security Check",     cls: "bg-blue-100 text-blue-700" },
  processing_preview:      { label: "Generating Preview", cls: "bg-blue-100 text-blue-700" },
  extracting_text:         { label: "OCR Running",        cls: "bg-blue-100 text-blue-700" },
};
const DOC_REVIEW_BADGE = {
  needs_replacement:   { label: "Needs Replacement", cls: "bg-amber-100 text-amber-700" },
  duplicate:           { label: "Duplicate",         cls: "bg-orange-100 text-orange-700" },
  superseded:          { label: "Superseded",        cls: "bg-slate-100 text-slate-400" },
  needs_clarification: { label: "Clarification",     cls: "bg-indigo-100 text-indigo-700" },
  not_needed:          { label: "Not Needed",        cls: "bg-slate-100 text-slate-500" },
};

// ─── Folder Tab Definitions ───────────────────────────────────────────────────
const FOLDER_TABS = [
  { key: "uploaded",     label: "Uploaded Documents",   icon: "Files",       color: "blue"   },
  { key: "requested",    label: "Requested Documents",  icon: "FolderInput", color: "amber"  },
  { key: "health",       label: "Document Health",      icon: "HeartPulse",  color: "green"  },
  { key: "intelligence", label: "Package Intelligence", icon: "Sparkles",    color: "violet" },
  { key: "advisories",   label: "Missing Advisories",   icon: "AlertCircle", color: "rose"   },
];

// Per-tab pastel color tokens
const TAB_COLORS = {
  blue:   { activeBg: "bg-blue-50",   activeBorder: "border-blue-200",   activeText: "text-blue-900",   activeBadge: "bg-blue-200 text-blue-700"   },
  amber:  { activeBg: "bg-amber-50",  activeBorder: "border-amber-200",  activeText: "text-amber-900",  activeBadge: "bg-amber-200 text-amber-700"  },
  green:  { activeBg: "bg-green-50",  activeBorder: "border-green-200",  activeText: "text-green-900",  activeBadge: "bg-green-200 text-green-700"  },
  violet: { activeBg: "bg-violet-50", activeBorder: "border-violet-200", activeText: "text-violet-900", activeBadge: "bg-violet-200 text-violet-700" },
  rose:   { activeBg: "bg-rose-50",   activeBorder: "border-rose-200",   activeText: "text-rose-900",   activeBadge: "bg-rose-200 text-rose-700"   },
};

const STATUS_TONES = {
  uploaded: "success", approved: "success", awaiting_review: "warning",
  missing: "danger", rejected: "danger", requested: "info",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function docBadge(d) {
  if (DOC_PROC_BADGE[d.processing_status]) return DOC_PROC_BADGE[d.processing_status];
  const rs = d.review_status || (d.status === "approved" ? "accepted" : d.status);
  if (DOC_REVIEW_BADGE[rs]) return DOC_REVIEW_BADGE[rs];
  if (rs === "needs_revision") return { label: "Needs Revision", cls: "bg-amber-100 text-amber-700" };
  return null;
}
function docMaxYear(d) {
  const years = d.tax_years || (d.tax_year ? [String(d.tax_year)] : []);
  return years.length ? Math.max(...years.map((y) => parseInt(y, 10) || 0)) : 0;
}
function sortDocs(arr) {
  return [...arr].sort((a, b) => {
    const diff = docMaxYear(b) - docMaxYear(a);
    return diff !== 0 ? diff : (b.created_at || "") > (a.created_at || "") ? 1 : -1;
  });
}

// ─── Sub-Components ───────────────────────────────────────────────────────────
function DocActionMenu({ doc, onAction }) {
  const isNotNeeded = doc.review_status === "not_needed";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button onClick={(e) => e.stopPropagation()}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors shrink-0"
          title="Document actions" data-testid={`ws-doc-actions-${doc.id}`} aria-label="Document actions">
          <Icons.MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
        {!isNotNeeded && (
          <>
            <DropdownMenuItem onClick={() => onAction("reclassify", doc)} data-testid={`ws-doc-reclassify-${doc.id}`}>
              <Icons.FolderEdit className="h-4 w-4 mr-2" />Reclassify
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction("change_year", doc)} data-testid={`ws-doc-year-${doc.id}`}>
              <Icons.Calendar className="h-4 w-4 mr-2" />Change Tax Year
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onAction("not_needed", doc)}
              className="text-amber-700 focus:text-amber-700" data-testid={`ws-doc-not-needed-${doc.id}`}>
              <Icons.EyeOff className="h-4 w-4 mr-2" />Mark Not Needed
            </DropdownMenuItem>
          </>
        )}
        {isNotNeeded && (
          <DropdownMenuItem onClick={() => onAction("restore", doc)} data-testid={`ws-doc-restore-${doc.id}`}>
            <Icons.RotateCcw className="h-4 w-4 mr-2" />Restore Document
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DocRow({ d, onOpen, onAction, showCategory = false }) {
  const badge = docBadge(d);
  const years = d.tax_years || (d.tax_year ? [String(d.tax_year)] : []);
  const isTaxYearConfirmed = (d.staff_confirmed_metadata_fields || []).includes("tax_year");
  const dlUrl = `${API}/documents-intel/${d.id}/content?disposition=attachment&auth=${encodeURIComponent(getToken() || "")}`;
  const ext = (d.file_extension || "").toLowerCase();
  const DocIcon = ["jpg","jpeg","png","webp","gif","bmp"].includes(ext) ? Icons.Image
    : ["xls","xlsx","ods","csv"].includes(ext) ? Icons.Table : Icons.FileText;
  return (
    <div role="button" tabIndex={0}
      onClick={() => onOpen(d.id)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(d.id)}
      className="flex items-center gap-2.5 justify-between border border-slate-100 rounded-lg px-3 py-2 cursor-pointer hover:border-blue-300 hover:bg-slate-50 transition-colors"
      data-testid={`ws-doc-row-${d.id}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
        <DocIcon className="h-4 w-4 text-slate-400 shrink-0" />
        <span className="text-sm text-slate-800 truncate" data-testid={`ws-doc-name-${d.id}`}>{d.display_filename}</span>
        {d.version > 1 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">
            v{d.version}{!d.is_superseded ? " Latest" : ""}
          </span>
        )}
        {d.is_superseded && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 shrink-0">Superseded</span>}
        {d.possible_duplicate && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 shrink-0" data-testid={`ws-doc-dup-${d.id}`}>Possible Duplicate</span>
        )}
        {showCategory && d.category && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0 hidden sm:inline">
            {CAT_LABELS[d.category] || d.category}
          </span>
        )}
        {years.length > 0 && (
          isTaxYearConfirmed
            ? <span className="inline-flex items-center text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded shrink-0" data-testid={`ws-doc-year-chip-${d.id}`}>{years.slice(0, 2).join(", ")}</span>
            : <span className="text-[10px] text-slate-400 shrink-0" data-testid={`ws-doc-year-chip-${d.id}`}>{years.slice(0, 2).join(", ")}</span>
        )}
        {years.length === 0 && isTaxYearConfirmed && (
          <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded shrink-0" data-testid={`ws-doc-na-${d.id}`}>N/A</span>
        )}
        {badge && <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${badge.cls}`} data-testid={`ws-doc-status-${d.id}`}>{badge.label}</span>}
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <button onClick={(e) => { e.stopPropagation(); window.open(dlUrl, "_blank"); }}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-400 shrink-0"
          title="Download original" data-testid={`ws-doc-dl-${d.id}`}
          aria-label={`Download ${d.display_filename}`}>
          <Icons.Download className="h-4 w-4" />
        </button>
        {onAction && <DocActionMenu doc={d} onAction={onAction} />}
      </div>
    </div>
  );
}

function DocGroup({ catKey, label, count, docs, isCollapsed, onToggle, onOpen, onAction, tone }) {
  return (
    <div className="border border-slate-100 rounded-lg overflow-hidden" data-testid={`ws-doc-group-${catKey}`}>
      <button onClick={onToggle}
        className={`w-full flex items-center justify-between px-3 py-2.5 ${tone === "amber" ? "bg-amber-50 hover:bg-amber-100" : tone === "slate" ? "bg-slate-100 hover:bg-slate-200" : "bg-slate-50 hover:bg-slate-100"} transition-colors text-left`}
        data-testid={`ws-doc-group-toggle-${catKey}`} aria-expanded={!isCollapsed}>
        <div className="flex items-center gap-2">
          {isCollapsed ? <Icons.ChevronRight className="h-3.5 w-3.5 text-slate-400" /> : <Icons.ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
          <span className="text-sm font-medium text-slate-700">{label}</span>
          <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${tone === "amber" ? "bg-amber-200 text-amber-700" : tone === "slate" ? "bg-slate-300 text-slate-600" : "bg-slate-200 text-slate-500"}`}
            data-testid={`ws-doc-group-count-${catKey}`}>{count}</span>
        </div>
      </button>
      {!isCollapsed && (
        <div className="px-1 py-1 space-y-1">
          {docs.map((d) => <DocRow key={d.id} d={d} onOpen={onOpen} onAction={onAction} />)}
        </div>
      )}
    </div>
  );
}

// ─── Uploaded Documents Tab ───────────────────────────────────────────────────
function UploadedDocumentsContent({ clientId, clientName, docs, reload, onCountChange }) {
  const navigate = useNavigate();
  const VIEW_KEY = `ws-docs-view-${clientId}`;

  const [uploaded, setUploaded] = useState([]);
  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem(VIEW_KEY) || "sections");
  const [search, setSearch] = useState("");
  const [filterYear, setFilterYear] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [collapsed, setCollapsed] = useState({ __not_needed__: true, __na__: true, __unknown__: true });
  const [showStaffUpload, setShowStaffUpload] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionForm, setActionForm] = useState({});
  const [actionPending, setActionPending] = useState(false);

  const refetch = useCallback(() => {
    api.get(`/documents-intel/browse/all?client_id=${clientId}&page_size=200&include_archived=false`)
      .then((r) => {
        const items = r.data.items || [];
        setUploaded(items);
        onCountChange?.(items.filter((d) => d.review_status !== "not_needed" && d.review_status !== "duplicate").length);
      })
      .catch(() => setUploaded([]));
  }, [clientId, onCountChange]);
  useEffect(() => { refetch(); }, [refetch]);

  const switchView = (mode) => { setViewMode(mode); sessionStorage.setItem(VIEW_KEY, mode); };

  const onOpen = useCallback(
    (id) => navigate(`/documents/${id}`, { state: { returnTo: `/client-workspace/${clientId}?tab=documents` } }),
    [navigate, clientId]
  );

  const activeDocs = useMemo(() => uploaded.filter((d) => d.review_status !== "not_needed" && d.review_status !== "duplicate"), [uploaded]);
  const notNeededDocs = useMemo(() => uploaded.filter((d) => d.review_status === "not_needed" || d.review_status === "duplicate"), [uploaded]);
  const needsReviewDocs = useMemo(() =>
    activeDocs.filter((d) => ["needs_replacement", "review_required", "needs_clarification"].includes(d.review_status)),
    [activeDocs]);

  // Available tax years for the Year filter dropdown
  const availableYears = useMemo(() => {
    const years = new Set();
    activeDocs.forEach((d) => {
      const ys = d.tax_years || (d.tax_year ? [String(d.tax_year)] : []);
      ys.forEach((y) => { if (y) years.add(String(y)); });
    });
    return [...years].sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
  }, [activeDocs]);

  const filtered = useMemo(() => {
    let pool = activeDocs;
    const q = search.trim().toLowerCase();
    if (q) {
      pool = pool.filter((d) => {
        const years = d.tax_years || (d.tax_year ? [String(d.tax_year)] : []);
        return [d.display_filename, d.original_filename, d.document_type, d.category, ...years]
          .some((v) => v?.toString().toLowerCase().includes(q));
      });
    }
    if (filterYear !== "all") {
      pool = pool.filter((d) => {
        const years = d.tax_years || (d.tax_year ? [String(d.tax_year)] : []);
        return years.includes(filterYear);
      });
    }
    if (filterStatus === "needs_attention") {
      pool = pool.filter((d) => ["needs_replacement", "review_required", "needs_clarification"].includes(d.review_status));
    } else if (filterStatus === "available") {
      pool = pool.filter((d) => (d.technical_status || d.processing_status) === "available" || d.processing_status === "ready");
    } else if (filterStatus === "processing") {
      pool = pool.filter((d) => ["processing","uploaded","security_scanning","processing_preview","extracting_text"].includes(d.technical_status || d.processing_status));
    }
    return pool;
  }, [activeDocs, search, filterYear, filterStatus]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((d) => {
      const cat = d.category || "other";
      if (!g[cat]) g[cat] = [];
      g[cat].push(d);
    });
    Object.values(g).forEach((arr) => arr.sort((a, b) => {
      const diff = docMaxYear(b) - docMaxYear(a);
      return diff !== 0 ? diff : (b.created_at || "") > (a.created_at || "") ? 1 : -1;
    }));
    return g;
  }, [filtered]);

  const activeCats = useMemo(
    () => [...CAT_ORDER, ...Object.keys(grouped).filter((k) => !CAT_ORDER.includes(k))].filter((k) => (grouped[k]?.length || 0) > 0),
    [grouped]
  );

  const sectionDocs = useMemo(() => {
    const sections = {};
    SECTION_GROUPS.forEach((sg) => { sections[sg.key] = []; });
    const q = search.trim().toLowerCase();
    activeDocs.forEach((d) => {
      // Apply year/status filters
      if (filterYear !== "all") {
        const years = d.tax_years || (d.tax_year ? [String(d.tax_year)] : []);
        if (!years.includes(filterYear)) return;
      }
      if (filterStatus === "needs_attention" && !["needs_replacement", "review_required", "needs_clarification"].includes(d.review_status)) return;
      if (filterStatus === "available" && !((d.technical_status || d.processing_status) === "available" || d.processing_status === "ready")) return;
      if (filterStatus === "processing" && !["processing","uploaded","security_scanning","processing_preview","extracting_text"].includes(d.technical_status || d.processing_status)) return;
      if (q) {
        const years = d.tax_years || (d.tax_year ? [String(d.tax_year)] : []);
        const match = [d.display_filename, d.original_filename, d.document_type, d.category, ...years].some((v) => v?.toString().toLowerCase().includes(q));
        if (!match) return;
      }
      const sKey = CAT_TO_SECTION[d.category || ""] || "other";
      if (sections[sKey]) sections[sKey].push(d);
      else sections["other"].push(d);
    });
    Object.values(sections).forEach((arr) => arr.sort((a, b) => {
      const diff = docMaxYear(b) - docMaxYear(a);
      return diff !== 0 ? diff : (b.created_at || "") > (a.created_at || "") ? 1 : -1;
    }));
    return sections;
  }, [activeDocs, search, filterYear, filterStatus]);

  const taxYearGrouped = useMemo(() => {
    const g = {};
    filtered.forEach((d) => {
      const key = d.tax_year ? String(d.tax_year)
        : (d.staff_confirmed_metadata_fields || []).includes("tax_year") ? "__na__" : "__unknown__";
      if (!g[key]) g[key] = [];
      g[key].push(d);
    });
    Object.values(g).forEach((arr) => arr.sort((a, b) => (b.created_at || "") > (a.created_at || "") ? 1 : -1));
    return g;
  }, [filtered]);

  const taxYearKeys = useMemo(() => {
    const yearKeys = Object.keys(taxYearGrouped).filter((k) => k !== "__na__" && k !== "__unknown__").sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
    const result = [...yearKeys];
    if (taxYearGrouped["__na__"]) result.push("__na__");
    if (taxYearGrouped["__unknown__"]) result.push("__unknown__");
    return result;
  }, [taxYearGrouped]);

  const needsReviewInSearch = useMemo(() => {
    if (filterStatus !== "all" && filterStatus !== "needs_attention") return [];
    return filtered.filter((d) => ["needs_replacement", "review_required", "needs_clarification"].includes(d.review_status));
  }, [filtered, filterStatus]);

  const notNeededInSearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notNeededDocs;
    return notNeededDocs.filter((d) => {
      const years = d.tax_years || (d.tax_year ? [String(d.tax_year)] : []);
      return [d.display_filename, d.original_filename, d.document_type, d.category, ...years].some((v) => v?.toString().toLowerCase().includes(q));
    });
  }, [notNeededDocs, search]);

  const toggleCollapse = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  // Expand All / Collapse All — client-side only
  const allSectionKeys = useMemo(() => {
    const keys = ["__needs_review__", ...SECTION_GROUPS.map((sg) => sg.key), ...activeCats, "__not_needed__", "__na__", "__unknown__"];
    return [...new Set(keys)];
  }, [activeCats]);

  const expandAll = useCallback(() => {
    setCollapsed((prev) => {
      const next = { ...prev };
      allSectionKeys.forEach((k) => { next[k] = false; });
      return next;
    });
  }, [allSectionKeys]);

  const collapseAll = useCallback(() => {
    setCollapsed((prev) => {
      const next = { ...prev };
      allSectionKeys.forEach((k) => { next[k] = true; });
      return next;
    });
  }, [allSectionKeys]);

  const handleAction = useCallback(async (type, doc) => {
    if (type === "reclassify") {
      setActionForm({ category: doc.category || "", document_type: doc.document_type || "" });
      setActionDialog({ type: "reclassify", doc });
    } else if (type === "change_year") {
      setActionForm({ tax_year: doc.tax_year ? String(doc.tax_year) : "" });
      setActionDialog({ type: "change_year", doc });
    } else if (type === "not_needed") {
      const name = doc.display_filename || doc.original_filename;
      if (!window.confirm(`Mark "${name}" as Not Needed?\n\nThe document will be preserved and recoverable, but removed from the active workspace.`)) return;
      try {
        await api.post(`/documents-intel/${doc.id}/review/action`, { action: "mark_not_needed" });
        toast.success("Document moved to Not Needed");
        refetch();
      } catch (e) { toast.error(e.response?.data?.detail || "Could not update document"); }
    } else if (type === "restore") {
      try {
        await api.post(`/documents-intel/${doc.id}/review/action`, { action: "restore_from_not_needed" });
        toast.success("Document restored to active workspace");
        refetch();
      } catch (e) { toast.error(e.response?.data?.detail || "Could not restore document"); }
    }
  }, [refetch]);

  const submitAction = useCallback(async () => {
    if (!actionDialog) return;
    setActionPending(true);
    try {
      if (actionDialog.type === "reclassify") {
        await api.patch(`/documents-intel/${actionDialog.doc.id}/metadata`, { category: actionForm.category || null, document_type: actionForm.document_type || null });
        toast.success("Document reclassified");
      } else if (actionDialog.type === "change_year") {
        await api.patch(`/documents-intel/${actionDialog.doc.id}/metadata`, { tax_year: actionForm.tax_year || null });
        toast.success("Tax year updated");
      }
      setActionDialog(null);
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update document");
    } finally { setActionPending(false); }
  }, [actionDialog, actionForm, refetch]);

  const totalActive = activeDocs.length;
  const totalNotNeeded = notNeededDocs.length;

  // Mini stats for Uploaded tab
  const statAvailable = activeDocs.filter((d) => (d.technical_status || d.processing_status) === "available" || d.processing_status === "ready").length;
  const statProcessing = activeDocs.filter((d) => ["processing","uploaded","security_scanning","processing_preview","extracting_text"].includes(d.technical_status || d.processing_status)).length;

  return (
    <div className="p-4 space-y-4" data-testid="ws-uploaded-docs-panel">
      {/* Compact stats strip */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {[
          { label: "Requested",     value: docs?.counts?.requested || 0,   tone: "text-blue-700",    bg: "bg-blue-50",    icon: "FileText" },
          { label: "Available",     value: statAvailable,                  tone: "text-emerald-700", bg: "bg-emerald-50", icon: "CheckCircle" },
          { label: "Processing",    value: statProcessing,                 tone: "text-blue-600",    bg: "bg-blue-50",    icon: "Loader2" },
          { label: "Needs Attention",value: needsReviewDocs.length,        tone: "text-amber-700",   bg: "bg-amber-50",   icon: "AlertCircle" },
          { label: "Missing",       value: docs?.counts?.missing || 0,     tone: "text-rose-700",    bg: "bg-rose-50",    icon: "FileX" },
        ].map(({ label, value, tone, bg, icon }) => {
          const I = Icons[icon];
          return (
            <div key={label} className={`${bg} rounded-lg p-2.5 flex items-center gap-2`}
              data-testid={`ws-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              {I && <I className={`h-4 w-4 ${tone} shrink-0`} />}
              <div>
                <div className={`text-base font-semibold leading-none ${tone}`}>{value}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Toolbar: search + filters + upload */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px]">
          <Icons.Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by filename, type, category, or year…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
            data-testid="ws-doc-search" />
        </div>

        {/* Group-by dropdown */}
        <Select value={viewMode} onValueChange={switchView}>
          <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="ws-docs-groupby">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sections">Sections</SelectItem>
            <SelectItem value="grouped">Categories</SelectItem>
            <SelectItem value="tax_year">Tax Year</SelectItem>
            <SelectItem value="all">All Documents</SelectItem>
          </SelectContent>
        </Select>

        {/* Year filter */}
        <Select value={filterYear} onValueChange={setFilterYear}>
          <SelectTrigger className="w-[110px] h-9 text-xs" data-testid="ws-docs-filter-year">
            <SelectValue placeholder="All Years" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {availableYears.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Status filter */}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="ws-docs-filter-status">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="needs_attention">Needs Attention</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
          </SelectContent>
        </Select>

        {/* Expand All / Collapse All */}
        {viewMode !== "all" && uploaded.length > 0 && (
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-1 py-0.5">
            <button onClick={expandAll}
              className="text-xs px-2 py-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
              data-testid="ws-docs-expand-all">Expand All</button>
            <span className="text-slate-200">|</span>
            <button onClick={collapseAll}
              className="text-xs px-2 py-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
              data-testid="ws-docs-collapse-all">Collapse All</button>
          </div>
        )}

        {/* Upload */}
        <button onClick={() => setShowStaffUpload(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shrink-0"
          data-testid="ws-staff-upload-btn">
          <Icons.Upload className="h-4 w-4" />
          Upload
        </button>
      </div>

      {/* Document list */}
      {uploaded.length === 0 ? (
        <EmptyState icon="Files" title="No documents uploaded yet" />
      ) : (activeDocs.length === 0 && notNeededDocs.length > 0) ? (
        <div className="text-sm text-slate-500 py-4 text-center">All documents are marked Not Needed. See the Not Needed section below.</div>
      ) : viewMode === "sections" ? (
        <div className="space-y-2" data-testid="ws-uploaded-docs-sections">
          {needsReviewInSearch.length > 0 && (
            <DocGroup catKey="__needs_review__" label="Needs Attention" count={needsReviewInSearch.length}
              docs={needsReviewInSearch} isCollapsed={!!collapsed["__needs_review__"]}
              onToggle={() => toggleCollapse("__needs_review__")} onOpen={onOpen} onAction={handleAction} tone="amber" />
          )}
          {SECTION_GROUPS.map((sg) => {
            const needsReviewIds = new Set(needsReviewInSearch.map((d) => d.id));
            const mainItems = (sectionDocs[sg.key] || []).filter((d) => !needsReviewIds.has(d.id));
            if (mainItems.length === 0) return null;
            return (
              <DocGroup key={sg.key} catKey={sg.key} label={sg.label} count={mainItems.length}
                docs={mainItems} isCollapsed={!!collapsed[sg.key]}
                onToggle={() => toggleCollapse(sg.key)} onOpen={onOpen} onAction={handleAction} />
            );
          })}
          {notNeededInSearch.length > 0 && (
            <DocGroup catKey="__not_needed__" label="Not Needed" count={notNeededInSearch.length}
              docs={notNeededInSearch} isCollapsed={collapsed["__not_needed__"] !== false}
              onToggle={() => toggleCollapse("__not_needed__")} onOpen={onOpen} onAction={handleAction} tone="slate" />
          )}
        </div>
      ) : viewMode === "grouped" ? (
        <div className="space-y-2" data-testid="ws-uploaded-docs-grouped">
          {needsReviewInSearch.length > 0 && (
            <DocGroup catKey="__needs_review__" label="Needs Attention" count={needsReviewInSearch.length}
              docs={needsReviewInSearch} isCollapsed={!!collapsed["__needs_review__"]}
              onToggle={() => toggleCollapse("__needs_review__")} onOpen={onOpen} onAction={handleAction} tone="amber" />
          )}
          {activeCats.map((cat) => {
            const needsReviewIds = new Set(needsReviewInSearch.map((d) => d.id));
            const catItems = (grouped[cat] || []).filter((d) => !needsReviewIds.has(d.id));
            if (catItems.length === 0) return null;
            return (
              <DocGroup key={cat} catKey={cat} label={CAT_LABELS[cat] || cat} count={catItems.length}
                docs={catItems} isCollapsed={!!collapsed[cat]}
                onToggle={() => toggleCollapse(cat)} onOpen={onOpen} onAction={handleAction} />
            );
          })}
          {notNeededInSearch.length > 0 && (
            <DocGroup catKey="__not_needed__" label="Not Needed" count={notNeededInSearch.length}
              docs={notNeededInSearch} isCollapsed={collapsed["__not_needed__"] !== false}
              onToggle={() => toggleCollapse("__not_needed__")} onOpen={onOpen} onAction={handleAction} tone="slate" />
          )}
        </div>
      ) : viewMode === "tax_year" ? (
        <div className="space-y-2" data-testid="ws-uploaded-docs-year">
          {needsReviewInSearch.length > 0 && (
            <DocGroup catKey="__needs_review__" label="Needs Attention" count={needsReviewInSearch.length}
              docs={needsReviewInSearch} isCollapsed={!!collapsed["__needs_review__"]}
              onToggle={() => toggleCollapse("__needs_review__")} onOpen={onOpen} onAction={handleAction} tone="amber" />
          )}
          {taxYearKeys.map((key) => {
            const needsReviewIds = new Set(needsReviewInSearch.map((d) => d.id));
            const items = (taxYearGrouped[key] || []).filter((d) => !needsReviewIds.has(d.id));
            if (items.length === 0) return null;
            const label = key === "__na__" ? "N/A — Not Tax-Year Specific" : key === "__unknown__" ? "Unknown" : `Tax Year ${key}`;
            const isSpecial = key === "__na__" || key === "__unknown__";
            const isCollapsedState = isSpecial ? collapsed[key] !== false : !!collapsed[key];
            return (
              <DocGroup key={key} catKey={key} label={label} count={items.length}
                docs={items} isCollapsed={isCollapsedState}
                onToggle={() => toggleCollapse(key)} onOpen={onOpen} onAction={handleAction}
                tone={isSpecial ? "slate" : undefined} />
            );
          })}
          {notNeededInSearch.length > 0 && (
            <DocGroup catKey="__not_needed__" label="Not Needed" count={notNeededInSearch.length}
              docs={notNeededInSearch} isCollapsed={collapsed["__not_needed__"] !== false}
              onToggle={() => toggleCollapse("__not_needed__")} onOpen={onOpen} onAction={handleAction} tone="slate" />
          )}
        </div>
      ) : (
        <div className="space-y-1" data-testid="ws-uploaded-docs-all">
          {sortDocs(filtered).map((d) => <DocRow key={d.id} d={d} onOpen={onOpen} onAction={handleAction} showCategory />)}
          {notNeededInSearch.length > 0 && (
            <>
              <div className="pt-3 pb-1 text-xs font-medium text-slate-400 uppercase tracking-wider px-1">Not Needed ({notNeededInSearch.length})</div>
              {sortDocs(notNeededInSearch).map((d) => <DocRow key={d.id} d={d} onOpen={onOpen} onAction={handleAction} showCategory />)}
            </>
          )}
        </div>
      )}

      {/* Reclassify dialog */}
      {actionDialog?.type === "reclassify" && (
        <Dialog open onOpenChange={() => setActionDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Reclassify Document</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-xs text-slate-500 truncate">{actionDialog.doc.display_filename}</p>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Category</label>
                <Select value={actionForm.category || undefined} onValueChange={(v) => setActionForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="ws-reclassify-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="max-h-56">
                    {Object.entries(CAT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setActionDialog(null)} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
              <button onClick={submitAction} disabled={actionPending || !actionForm.category}
                className="px-4 py-1.5 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50"
                data-testid="ws-reclassify-submit">{actionPending ? "Saving…" : "Save"}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Change Year dialog */}
      {actionDialog?.type === "change_year" && (
        <Dialog open onOpenChange={() => setActionDialog(null)}>
          <DialogContent className="max-w-xs">
            <DialogHeader><DialogTitle>Change Tax Year</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-xs text-slate-500 truncate">{actionDialog.doc.display_filename}</p>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Tax Year</label>
                <input type="number" min="2000" max="2099" value={actionForm.tax_year || ""}
                  onChange={(e) => setActionForm((f) => ({ ...f, tax_year: e.target.value }))}
                  placeholder="e.g. 2024"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="ws-change-year-input" />
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setActionDialog(null)} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
              <button onClick={submitAction} disabled={actionPending}
                className="px-4 py-1.5 text-sm bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50"
                data-testid="ws-change-year-submit">{actionPending ? "Saving…" : "Save"}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <StaffUploadDialog open={showStaffUpload} onClose={() => setShowStaffUpload(false)}
        onDone={() => { setShowStaffUpload(false); refetch(); reload?.(); }}
        clientId={clientId} clientName={clientName} />
    </div>
  );
}

// ─── Requested Documents Tab ──────────────────────────────────────────────────
function RequestedDocumentsContent({ clientId, docs, reload }) {
  const navigate = useNavigate();
  const [uploadCtx, setUploadCtx] = useState(null);

  const requests = docs?.requests || [];

  const onOpen = useCallback(
    (id) => navigate(`/documents/${id}`, { state: { returnTo: `/client-workspace/${clientId}?tab=documents` } }),
    [navigate, clientId]
  );

  const waive = async (r) => {
    const reason = window.prompt(`Waive "${r.name || r.title}"? Enter a reason:`);
    if (!reason?.trim()) return;
    try { await api.post(`/documents-intel/requests/${r.id}/waive`, { reason: reason.trim() }); toast.success("Request waived"); reload?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not waive"); }
  };

  return (
    <div data-testid="ws-requested-docs-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">{requests.length} document request{requests.length !== 1 ? "s" : ""}</p>
        <button onClick={() => setUploadCtx({})}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
          data-testid="ws-doc-upload">
          <Icons.Upload className="h-4 w-4" />Upload
        </button>
      </div>

      <DataTable
        columns={[
          { key: "name",           label: "Document" },
          { key: "document_type",  label: "Type" },
          { key: "request_status", label: "Status" },
          { key: "actions",        label: "", align: "right" },
        ]}
        rows={requests}
        renderCell={(r, col) => {
          if (col.key === "request_status") return <StatusBadge status={r.request_status} map={STATUS_TONES} />;
          if (col.key === "document_type") return <Pill tone="neutral">{r.document_type_label || r.document_type}</Pill>;
          if (col.key === "actions") {
            if (r.request_status === "awaiting_review" && r.linked_document_id) {
              return (
                <button onClick={() => onOpen(r.linked_document_id)} data-testid={`ws-req-review-${r.id}`}
                  className="text-[11px] text-amber-700 hover:underline inline-flex items-center gap-1">
                  <Icons.Eye className="h-3 w-3" />Review
                </button>
              );
            }
            if (["missing", "requested", "needs_revision"].includes(r.request_status)) {
              return (
                <div className="inline-flex items-center gap-2 justify-end">
                  <button onClick={() => setUploadCtx({ requestId: r.id, requestLabel: r.name || r.title })}
                    data-testid={`ws-req-upload-${r.id}`}
                    className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1">
                    <Icons.Upload className="h-3 w-3" />Upload
                  </button>
                  <button onClick={() => waive(r)} data-testid={`ws-waive-${r.id}`}
                    className="text-[11px] text-amber-600 hover:underline">Waive</button>
                </div>
              );
            }
            return null;
          }
          return (
            <div>
              <span className="text-slate-800">{r.title || r.name}</span>
              {["missing", "requested", "needs_revision"].includes(r.request_status) && (
                <ExistingDocumentReuse requestId={r.id} clientId={clientId} onUsed={() => reload?.()} />
              )}
            </div>
          );
        }}
        empty={<EmptyState icon="FolderOpen" title="No document requests" />}
      />

      {uploadCtx && (
        <LockedUploadDialog open onClose={() => setUploadCtx(null)}
          onDone={() => { setUploadCtx(null); reload?.(); }}
          clientId={clientId} requestId={uploadCtx.requestId} requestLabel={uploadCtx.requestLabel}
          title={uploadCtx.requestId ? "Upload for Request" : "Upload Documents"} />
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function WorkspaceDocumentCenter({ clientId, clientName, docs, reload }) {
  const STORAGE_KEY = `tf.ws.docTab.${clientId}`;

  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "uploaded"; } catch { return "uploaded"; }
  });

  // Badge/indicator data for tab headers
  const [healthData,         setHealthData]         = useState(null);
  const [advisoriesCount,    setAdvisoriesCount]    = useState(null);
  const [intelligenceStatus, setIntelligenceStatus] = useState(null);
  const [uploadedCount,      setUploadedCount]      = useState(null);

  useEffect(() => {
    api.get(`/documents-intel/package/${clientId}/health`).then((r) => setHealthData(r.data)).catch(() => {});
    api.get(`/document-advisories?client_id=${clientId}&limit=200`)
      .then((r) => setAdvisoriesCount((r.data?.advisories || r.data?.items || []).length)).catch(() => {});
    api.get(`/documents-intel/package/${clientId}/status`).then((r) => setIntelligenceStatus(r.data)).catch(() => {});
  }, [clientId]);

  // Persist selected folder tab per client
  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    try { localStorage.setItem(STORAGE_KEY, key); } catch {}
  }, [STORAGE_KEY]);

  // Keyboard arrow navigation within the tab bar
  const handleKeyDown = useCallback((e) => {
    const keys = FOLDER_TABS.map((t) => t.key);
    const idx = keys.indexOf(activeTab);
    if (e.key === "ArrowRight" && idx < keys.length - 1) { e.preventDefault(); handleTabChange(keys[idx + 1]); }
    else if (e.key === "ArrowLeft" && idx > 0) { e.preventDefault(); handleTabChange(keys[idx - 1]); }
    else if (e.key === "Home") { e.preventDefault(); handleTabChange(keys[0]); }
    else if (e.key === "End") { e.preventDefault(); handleTabChange(keys[keys.length - 1]); }
  }, [activeTab, handleTabChange]);

  // Tab count / indicator values
  const tabBadge = (key) => {
    if (key === "uploaded") return uploadedCount !== null ? uploadedCount : (docs?.uploaded_files?.length ?? null);
    if (key === "requested") return docs?.counts?.requested ?? docs?.requests?.length ?? null;
    if (key === "advisories") return advisoriesCount;
    if (key === "health" && healthData) {
      const n = (healthData.need_classification || 0) + (healthData.needs_review || 0);
      return n > 0 ? n : (healthData.total > 0 ? 0 : null);
    }
    return null;
  };

  const activeColors = TAB_COLORS[FOLDER_TABS.find((t) => t.key === activeTab)?.color || "blue"];

  return (
    <div data-testid="workspace-document-center">
      {/* ── Folder Tab Bar ── */}
      <div
        className="flex items-end overflow-x-auto gap-0.5 pb-0 scrollbar-none"
        role="tablist"
        aria-label="Document Center"
        onKeyDown={handleKeyDown}
      >
        {FOLDER_TABS.map((tab) => {
          const isActive = tab.key === activeTab;
          const colors = TAB_COLORS[tab.color];
          const LucideIcon = Icons[tab.icon] || Icons.Folder;
          const count = tabBadge(tab.key);

          return (
            <button
              key={tab.key}
              role="tab"
              id={`folder-tab-${tab.key}`}
              aria-selected={isActive}
              aria-controls={`folder-panel-${tab.key}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => handleTabChange(tab.key)}
              data-testid={`folder-tab-${tab.key}`}
              className={[
                "flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium rounded-t-lg border",
                "transition-all duration-150 shrink-0 select-none whitespace-nowrap",
                isActive
                  ? `${colors.activeBg} ${colors.activeBorder} border-b-0 ${colors.activeText} shadow-sm relative z-10`
                  : "bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200 hover:text-slate-700",
              ].join(" ")}
              style={isActive ? { marginBottom: "-1px", paddingBottom: "calc(0.625rem + 1px)" } : {}}
            >
              <LucideIcon className="h-3.5 w-3.5 shrink-0" />
              <span>{tab.label}</span>

              {/* Package Intelligence: status dot instead of count */}
              {tab.key === "intelligence" && intelligenceStatus && (
                <span
                  className={`h-2 w-2 rounded-full shrink-0 ${
                    intelligenceStatus.session_status === "current" ? "bg-emerald-500" :
                    intelligenceStatus.session_status === "new_documents_available" ? "bg-amber-500" :
                    "bg-slate-300"
                  }`}
                  data-testid="folder-tab-intelligence-dot"
                  title={intelligenceStatus.session_status?.replace(/_/g, " ")}
                />
              )}

              {/* Document Health: "N items" or "OK" badge */}
              {tab.key === "health" && healthData && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                    isActive ? colors.activeBadge :
                    ((healthData.need_classification || 0) + (healthData.needs_review || 0)) > 0
                      ? "bg-amber-100 text-amber-700"
                      : "bg-slate-200 text-slate-500"
                  }`}
                  data-testid="folder-tab-health-badge"
                >
                  {((healthData.need_classification || 0) + (healthData.needs_review || 0)) > 0
                    ? `${(healthData.need_classification || 0) + (healthData.needs_review || 0)} items`
                    : healthData.total > 0 ? "OK" : "—"}
                </span>
              )}

              {/* All other tabs: numeric count badge */}
              {tab.key !== "intelligence" && tab.key !== "health" && count !== null && count !== undefined && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                    isActive ? colors.activeBadge : "bg-slate-200 text-slate-500"
                  }`}
                  data-testid={`folder-tab-${tab.key}-badge`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Content Panel ── */}
      <div
        className={`bg-white border rounded-b-xl rounded-tr-xl shadow-sm ${activeColors.activeBorder}`}
        data-testid="folder-content-panel"
      >
        {activeTab === "uploaded" && (
          <div id="folder-panel-uploaded" role="tabpanel" aria-labelledby="folder-tab-uploaded">
            <UploadedDocumentsContent
              clientId={clientId} clientName={clientName} docs={docs} reload={reload}
              onCountChange={setUploadedCount}
            />
          </div>
        )}
        {activeTab === "requested" && (
          <div id="folder-panel-requested" role="tabpanel" aria-labelledby="folder-tab-requested" className="p-4">
            <RequestedDocumentsContent clientId={clientId} docs={docs} reload={reload} />
          </div>
        )}
        {activeTab === "health" && (
          <div id="folder-panel-health" role="tabpanel" aria-labelledby="folder-tab-health" className="p-4">
            <DocumentHealthStrip clientId={clientId} />
          </div>
        )}
        {activeTab === "intelligence" && (
          <div id="folder-panel-intelligence" role="tabpanel" aria-labelledby="folder-tab-intelligence" className="p-4">
            <PackageIntelligencePanel clientId={clientId} />
          </div>
        )}
        {activeTab === "advisories" && (
          <div id="folder-panel-advisories" role="tabpanel" aria-labelledby="folder-tab-advisories" className="p-4">
            <MissingDocumentAdvisoriesPanel clientId={clientId} />
          </div>
        )}
      </div>
    </div>
  );
}
